import { TransportError } from 'ProdantixSdk/core/errors';
import { type FetchLike, makeAbortController, resolveFetch, trimTrailingSlash } from 'ProdantixSdk/core/http';
import { type SurveyUrlRule, urlMatches } from 'ProdantixSdk/core/surveys';
import type { EventProperties } from 'ProdantixSdk/core/types';

export const GUIDE_SHOWN = '$guide_shown';
export const GUIDE_STEP_VIEWED = '$guide_step_viewed';
export const GUIDE_STEP_MISSED = '$guide_step_missed';
export const GUIDE_COMPLETED = '$guide_completed';
export const GUIDE_DISMISSED = '$guide_dismissed';
export const CHECKLIST_TASK_DONE = '$checklist_task_done';

export type GuideKind = 'checklist' | 'tooltip' | 'tour';

export type GuideStepPlacement = 'auto' | 'bottom' | 'center' | 'left' | 'right' | 'top';

export interface GuideStep {
  anchor?: string | null;
  body?: string | null;
  event?: string | null;
  id: string;
  image?: string | null;
  link?: string | null;
  placement: GuideStepPlacement;
  selector?: string | null;
  title: string;
}

export interface GuideAppearance {
  accent?: string | null;
  doneLabel?: string | null;
  launcher: 'bottom_left' | 'bottom_right';
  nextLabel?: string | null;
}

export interface Guide {
  appearance: GuideAppearance;
  event?: string | null;
  id: string;
  iteration: number;
  kind: GuideKind;
  name: string;
  showOnce: boolean;
  steps: GuideStep[];
  urls: SurveyUrlRule[];
  waitDays: number;
}

export interface GuideMemory {
  done: string[];
  lastShownAt?: number;
  settled: string[];
}

export interface GuidesClientOptions {
  fetch?: FetchLike;
  host: string;
  projectKey: string;
  requestTimeoutMs?: number;
}

export class GuidesClient {
  private readonly fetch?: FetchLike;
  private readonly host: string;
  private readonly projectKey: string;
  private readonly requestTimeoutMs: number;

  constructor(options: GuidesClientOptions) {
    this.fetch = resolveFetch(options.fetch);
    this.host = trimTrailingSlash(options.host);
    this.projectKey = options.projectKey;
    this.requestTimeoutMs = options.requestTimeoutMs ?? 10_000;
  }

  async guides(distinctId: string): Promise<Guide[]> {
    const fetchImpl = this.fetch;
    if (!fetchImpl) {
      throw new TransportError('no fetch implementation available in this runtime');
    }
    const controller = makeAbortController();
    const timer = controller ? setTimeout(() => controller.abort(), this.requestTimeoutMs) : undefined;
    try {
      const response = await fetchImpl(`${this.host}/v1/guides?distinct_id=${encodeURIComponent(distinctId)}`, {
        headers: { authorization: `Bearer ${this.projectKey}` },
        method: 'GET',
        signal: controller?.signal,
      });
      if (!response.ok) {
        throw new TransportError(`guides request failed with status ${response.status}`, response.status);
      }
      const parsed = JSON.parse(await response.text()) as { guides?: Guide[] };
      return parsed.guides ?? [];
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    }
  }
}

const DAY_MS = 86_400_000;

export const guideKey = (guide: Pick<Guide, 'id' | 'iteration'>): string => `${guide.id}:${guide.iteration}`;

export const taskKey = (guide: Pick<Guide, 'id' | 'iteration'>, stepId: string): string => `${guideKey(guide)}:${stepId}`;

export function isGuideDue(guide: Guide, memory: GuideMemory, href: string, now: number, event?: string): boolean {
  if (memory.settled.includes(guideKey(guide))) {
    return false;
  }
  if (guide.showOnce && memory.settled.some(key => key.startsWith(`${guide.id}:`))) {
    return false;
  }
  if (guide.kind !== 'checklist' && guide.waitDays > 0 && memory.lastShownAt !== undefined && now - memory.lastShownAt < guide.waitDays * DAY_MS) {
    return false;
  }
  if (guide.event && guide.event !== event) {
    return false;
  }
  return urlMatches(guide.urls, href);
}

export const tasksDone = (guide: Guide, memory: GuideMemory): string[] => guide.steps.filter(step => memory.done.includes(taskKey(guide, step.id))).map(step => step.id);

export const taskFor = (guide: Guide, eventName: string): GuideStep | undefined => (guide.kind === 'checklist' ? guide.steps.find(step => step.event === eventName) : undefined);

export function guideProperties(guide: Guide, stepId?: string): EventProperties {
  const properties: EventProperties = { $guide_id: guide.id, $guide_iteration: guide.iteration, $guide_name: guide.name };
  if (stepId !== undefined) {
    properties.$guide_step_id = stepId;
  }
  return properties;
}
